import { CommercialPlanContextProps } from 'contexts-types';
import { usePersistedState } from 'hooks';
import { Props } from 'props';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { notification } from 'antd';
import { CommercialPlanController } from 'controllers';
import { useAuth } from './auth';
import { TypeUser } from 'types/enums';
import {
  ICommercialPlanViewModel,
  IFontRuleViewModel,
  IUserViewModel,
} from 'viewModels';
import {
  ICreateCommercialPlanInputModel,
  IUpdateCommercialPlanInputModel,
  IAddFontRuleInputModel,
  IUpdateFontRuleInputModel,
  IOrderedWithPaginationInputModel,
  IOrderFieldInputModel,
} from 'inputModels';
import { FilterProps } from 'props';
import { useQuery } from 'react-query';

const CommercialPlanContext = createContext<CommercialPlanContextProps>(
  {} as CommercialPlanContextProps
);

// Função utilitária para extrair mensagens de erro do backend
const extractErrorMessage = (error: any, defaultMessage: string): string => {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.errorMessage ||
    error?.response?.data?.error ||
    error?.message ||
    defaultMessage
  );
};

const CommercialPlan: React.FC<Props> = ({ children }) => {
  const { user, logged } = useAuth();
  const [commercialPlans, setCommercialPlans] = usePersistedState<
    ICommercialPlanViewModel[]
  >('@legal_audit_commercial_plans', []);
  const [orderBy, setOrder] = useState<IOrderFieldInputModel<string>[]>([]);
  const [filter, setFilter] = useState<FilterProps | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
  const [hasNextPage, setHasNextPage] = useState<boolean | undefined>(
    undefined
  );
  const [page, setPage] = useState(1);
  const [userCommercialPlan, setUserCommercialPlan] =
    useState<ICommercialPlanViewModel | null>(null);
  const [isUserPlanLoaded, setIsUserPlanLoaded] = useState(false);

  const handleFetch = useCallback(async () => {
    // Verifica se o usuário está logado e é do tipo ADM
    if (!user || user.typeUser !== TypeUser.ADM) {
      return;
    }

    try {
      setIsLoading(true);
      const input: IOrderedWithPaginationInputModel<string> = {
        page: 1,
        take: 10,
        orderBy,
        filter: filter?.filter,
        term: filter?.term,
      };

      const response = await CommercialPlanController.getAll(input);
      if (response.success) {
        setCommercialPlans(response.data);
        setHasNextPage(response.data.length === 10);
        setPage(1);
      }
    } catch (error: any) {
      const errorMessage = extractErrorMessage(
        error,
        'Erro ao carregar planos comerciais'
      );
      notification.error({
        message: 'Erro',
        description: errorMessage,
        placement: 'topRight',
        pauseOnHover: true,
        showProgress: true,
        duration: 5,
      });
    } finally {
      setIsLoading(false);
    }
  }, [user, orderBy, filter, setCommercialPlans]);

  const handleFetchNextPage = useCallback(async () => {
    // Verifica se o usuário está logado e é do tipo ADM
    if (!user || user.typeUser !== TypeUser.ADM) {
      return;
    }

    if (isFetchingNextPage || !hasNextPage) return;

    try {
      setIsFetchingNextPage(true);
      const input: IOrderedWithPaginationInputModel<string> = {
        page: page + 1,
        take: 10,
        orderBy,
        filter: filter?.filter,
        term: filter?.term,
      };

      const response = await CommercialPlanController.getAll(input);
      if (response.success) {
        setCommercialPlans(prev => [...prev, ...response.data]);
        setHasNextPage(response.data.length === 10);
        setPage(prev => prev + 1);
      }
    } catch (error: any) {
      const errorMessage = extractErrorMessage(
        error,
        'Erro ao carregar mais planos comerciais'
      );
      notification.error({
        message: 'Erro',
        description: errorMessage,
        placement: 'topRight',
        pauseOnHover: true,
        showProgress: true,
        duration: 5,
      });
    } finally {
      setIsFetchingNextPage(false);
    }
  }, [
    user,
    isFetchingNextPage,
    hasNextPage,
    page,
    orderBy,
    filter,
    setCommercialPlans,
  ]);

  const handleCreatePlan = useCallback(
    async (input: ICreateCommercialPlanInputModel) => {
      // Verifica se o usuário está logado e é do tipo ADM
      if (!user || user.typeUser !== TypeUser.ADM) {
        notification.error({
          message: 'Erro',
          description:
            'Acesso negado. Apenas administradores podem criar planos comerciais.',
          placement: 'topRight',
          pauseOnHover: true,
          showProgress: true,
          duration: 5,
        });
        return false;
      }

      try {
        const response = await CommercialPlanController.create(input);
        if (response.success) {
          notification.success({
            message: 'Sucesso',
            description: 'Plano comercial criado com sucesso',
            placement: 'topRight',
            pauseOnHover: true,
            showProgress: true,
            duration: 3,
          });
          await handleFetch();
          return true;
        }
        return false;
      } catch (error: any) {
        const errorMessage = extractErrorMessage(
          error,
          'Erro ao criar plano comercial'
        );
        notification.error({
          message: 'Erro',
          description: errorMessage,
          placement: 'topRight',
          pauseOnHover: true,
          showProgress: true,
          duration: 5,
        });
        return false;
      }
    },
    [user, handleFetch]
  );

  const handleUpdatePlan = useCallback(
    async (id: string, input: IUpdateCommercialPlanInputModel) => {
      // Verifica se o usuário está logado e é do tipo ADM
      if (!user || user.typeUser !== TypeUser.ADM) {
        notification.error({
          message: 'Erro',
          description:
            'Acesso negado. Apenas administradores podem atualizar planos comerciais.',
          placement: 'topRight',
          pauseOnHover: true,
          showProgress: true,
          duration: 5,
        });
        return false;
      }

      try {
        const response = await CommercialPlanController.update(id, input);
        if (response.success) {
          notification.success({
            message: 'Sucesso',
            description: 'Plano comercial atualizado com sucesso',
            placement: 'topRight',
            pauseOnHover: true,
            showProgress: true,
            duration: 3,
          });
          await handleFetch();
          return true;
        }
        return false;
      } catch (error: any) {
        const errorMessage = extractErrorMessage(
          error,
          'Erro ao atualizar plano comercial'
        );
        notification.error({
          message: 'Erro',
          description: errorMessage,
          placement: 'topRight',
          pauseOnHover: true,
          showProgress: true,
          duration: 5,
        });
        return false;
      }
    },
    [user, handleFetch]
  );

  const handleRemovePlan = useCallback(
    async (id: string) => {
      // Verifica se o usuário está logado e é do tipo ADM
      if (!user || user.typeUser !== TypeUser.ADM) {
        notification.error({
          message: 'Erro',
          description:
            'Acesso negado. Apenas administradores podem remover planos comerciais.',
          placement: 'topRight',
          pauseOnHover: true,
          showProgress: true,
          duration: 5,
        });
        return false;
      }

      try {
        const response = await CommercialPlanController.remove(id);
        if (response.success) {
          notification.success({
            message: 'Sucesso',
            description: 'Plano comercial removido com sucesso',
            placement: 'topRight',
            pauseOnHover: true,
            showProgress: true,
            duration: 3,
          });
          await handleFetch();
          return true;
        }
        return false;
      } catch (error: any) {
        const errorMessage = extractErrorMessage(
          error,
          'Erro ao remover plano comercial'
        );
        notification.error({
          message: 'Erro',
          description: errorMessage,
          placement: 'topRight',
          pauseOnHover: true,
          showProgress: true,
          duration: 5,
        });
        return false;
      }
    },
    [user, handleFetch]
  );

  const handleGetPlanFontRules = useCallback(
    async (planId: string) => {
      // Verifica se o usuário está logado e é do tipo ADM
      if (!user || user.typeUser !== TypeUser.ADM) {
        return [];
      }

      try {
        const response = await CommercialPlanController.getPlanFontRules(
          planId
        );
        if (response.success) {
          return response.data;
        }
        return [];
      } catch (error: any) {
        const errorMessage = extractErrorMessage(
          error,
          'Erro ao carregar regras de fontes do plano'
        );
        notification.error({
          message: 'Erro',
          description: errorMessage,
          placement: 'topRight',
          pauseOnHover: true,
          showProgress: true,
          duration: 5,
        });
        return [];
      }
    },
    [user]
  );

  const handleAddFontRule = useCallback(
    async (planId: string, input: IAddFontRuleInputModel) => {
      // Verifica se o usuário está logado e é do tipo ADM
      if (!user || user.typeUser !== TypeUser.ADM) {
        notification.error({
          message: 'Erro',
          description:
            'Acesso negado. Apenas administradores podem adicionar regras de fontes.',
          placement: 'topRight',
          pauseOnHover: true,
          showProgress: true,
          duration: 5,
        });
        return false;
      }

      try {
        const response = await CommercialPlanController.addFontRule(
          planId,
          input
        );
        if (response.success) {
          notification.success({
            message: 'Sucesso',
            description: 'Regra de fonte adicionada com sucesso',
            placement: 'topRight',
            pauseOnHover: true,
            showProgress: true,
            duration: 3,
          });
          return true;
        }
        return false;
      } catch (error: any) {
        const errorMessage = extractErrorMessage(
          error,
          'Erro ao adicionar regra de fonte'
        );
        notification.error({
          message: 'Erro',
          description: errorMessage,
          placement: 'topRight',
          pauseOnHover: true,
          showProgress: true,
          duration: 5,
        });
        return false;
      }
    },
    [user]
  );

  const handleUpdateFontRule = useCallback(
    async (
      planId: string,
      fontKey: string,
      input: IUpdateFontRuleInputModel
    ) => {
      try {
        const response = await CommercialPlanController.updateFontRule(
          planId,
          fontKey,
          input
        );
        if (response.success) {
          notification.success({
            message: 'Sucesso',
            description: 'Regra de fonte atualizada com sucesso',
            placement: 'topRight',
            pauseOnHover: true,
            showProgress: true,
            duration: 3,
          });
          return true;
        }
        return false;
      } catch (error: any) {
        const errorMessage = extractErrorMessage(
          error,
          'Erro ao atualizar regra de fonte'
        );
        notification.error({
          message: 'Erro',
          description: errorMessage,
          placement: 'topRight',
          pauseOnHover: true,
          showProgress: true,
          duration: 5,
        });
        return false;
      }
    },
    []
  );

  const handleRemoveFontRule = useCallback(
    async (planId: string, fontKey: string) => {
      try {
        const response = await CommercialPlanController.removeFontRule(
          planId,
          fontKey
        );
        if (response.success) {
          notification.success({
            message: 'Sucesso',
            description: 'Regra de fonte removida com sucesso',
            placement: 'topRight',
            pauseOnHover: true,
            showProgress: true,
            duration: 3,
          });
          return true;
        }
        return false;
      } catch (error: any) {
        const errorMessage = extractErrorMessage(
          error,
          'Erro ao remover regra de fonte'
        );
        notification.error({
          message: 'Erro',
          description: errorMessage,
          placement: 'topRight',
          pauseOnHover: true,
          showProgress: true,
          duration: 5,
        });
        return false;
      }
    },
    []
  );

  const handleSetAsDefault = useCallback(
    async (id: string) => {
      try {
        const response = await CommercialPlanController.setAsDefault(id);
        if (response.success) {
          notification.success({
            message: 'Sucesso',
            description: 'Plano definido como padrão com sucesso',
            placement: 'topRight',
            pauseOnHover: true,
            showProgress: true,
            duration: 3,
          });
          await handleFetch();
          return true;
        }
        return false;
      } catch (error: any) {
        const errorMessage = extractErrorMessage(
          error,
          'Erro ao definir plano como padrão'
        );
        notification.error({
          message: 'Erro',
          description: errorMessage,
          placement: 'topRight',
          pauseOnHover: true,
          showProgress: true,
          duration: 5,
        });
        return false;
      }
    },
    [handleFetch]
  );

  const handleActivate = useCallback(
    async (id: string) => {
      try {
        const response = await CommercialPlanController.activate(id);
        if (response.success) {
          notification.success({
            message: 'Sucesso',
            description: 'Plano ativado com sucesso',
            placement: 'topRight',
            pauseOnHover: true,
            showProgress: true,
            duration: 3,
          });
          await handleFetch();
          return true;
        }
        return false;
      } catch (error: any) {
        const errorMessage = extractErrorMessage(error, 'Erro ao ativar plano');
        notification.error({
          message: 'Erro',
          description: errorMessage,
          placement: 'topRight',
          pauseOnHover: true,
          showProgress: true,
          duration: 5,
        });
        return false;
      }
    },
    [handleFetch]
  );

  const handleDeactivate = useCallback(
    async (id: string) => {
      try {
        const response = await CommercialPlanController.deactivate(id);
        if (response.success) {
          notification.success({
            message: 'Sucesso',
            description: 'Plano desativado com sucesso',
            placement: 'topRight',
            pauseOnHover: true,
            showProgress: true,
            duration: 3,
          });
          await handleFetch();
          return true;
        }
        return false;
      } catch (error: any) {
        const errorMessage = extractErrorMessage(
          error,
          'Erro ao desativar plano'
        );
        notification.error({
          message: 'Erro',
          description: errorMessage,
          placement: 'topRight',
          pauseOnHover: true,
          showProgress: true,
          duration: 5,
        });
        return false;
      }
    },
    [handleFetch]
  );

  const handleGetPlanUsers = useCallback(
    async (planId: string) => {
      // Verifica se o usuário está logado e é do tipo ADM
      if (!user || user.typeUser !== TypeUser.ADM) {
        return [];
      }

      try {
        const input: IOrderedWithPaginationInputModel<string> = {
          page: 1,
          take: 100,
        };
        const response = await CommercialPlanController.getPlanUsers(
          planId,
          input
        );
        if (response.success) {
          return response.data;
        }
        return [];
      } catch (error: any) {
        const errorMessage = extractErrorMessage(
          error,
          'Erro ao carregar usuários do plano'
        );
        notification.error({
          message: 'Erro',
          description: errorMessage,
          placement: 'topRight',
          pauseOnHover: true,
          showProgress: true,
          duration: 5,
        });
        return [];
      }
    },
    [user]
  );

  const handleAddUserToPlan = useCallback(
    async (planId: string, userId: string) => {
      try {
        const response = await CommercialPlanController.addUserToPlan(
          planId,
          userId
        );
        if (response.success) {
          notification.success({
            message: 'Sucesso',
            description: 'Usuário adicionado ao plano com sucesso',
            placement: 'topRight',
            pauseOnHover: true,
            showProgress: true,
            duration: 3,
          });
          return true;
        }
        return false;
      } catch (error: any) {
        const errorMessage = extractErrorMessage(
          error,
          'Erro ao adicionar usuário ao plano'
        );
        notification.error({
          message: 'Erro',
          description: errorMessage,
          placement: 'topRight',
          pauseOnHover: true,
          showProgress: true,
          duration: 5,
        });
        return false;
      }
    },
    []
  );

  const handleRemoveUserFromPlan = useCallback(
    async (planId: string, userId: string) => {
      try {
        const response = await CommercialPlanController.removeUserFromPlan(
          planId,
          userId
        );
        if (response.success) {
          notification.success({
            message: 'Sucesso',
            description: 'Usuário removido do plano com sucesso',
            placement: 'topRight',
            pauseOnHover: true,
            showProgress: true,
            duration: 3,
          });
          return true;
        }
        return false;
      } catch (error: any) {
        const errorMessage = extractErrorMessage(
          error,
          'Erro ao remover usuário do plano'
        );
        notification.error({
          message: 'Erro',
          description: errorMessage,
          placement: 'topRight',
          pauseOnHover: true,
          showProgress: true,
          duration: 5,
        });
        return false;
      }
    },
    []
  );

  const handleGetUserCommercialPlan = useCallback(async (userId: string) => {
    try {
      const response = await CommercialPlanController.getUserCommercialPlan(
        userId
      );
      if (response.success) {
        setUserCommercialPlan(response.data);
        setIsUserPlanLoaded(true);
        return response.data;
      }
      return null;
    } catch (error: any) {
      const errorMessage = extractErrorMessage(
        error,
        'Erro ao buscar plano comercial do usuário'
      );
      notification.error({
        message: 'Erro',
        description: errorMessage,
        placement: 'topRight',
        pauseOnHover: true,
        showProgress: true,
        duration: 5,
      });
      return null;
    }
  }, []);

  const handleTransferUsers = useCallback(
    async (sourcePlanId: string, targetPlanId: string, userIds: string[]) => {
      try {
        const response = await CommercialPlanController.transferUsers(
          sourcePlanId,
          targetPlanId,
          userIds
        );
        if (response.success) {
          notification.success({
            message: 'Sucesso',
            description: 'Usuários transferidos com sucesso',
            placement: 'topRight',
            pauseOnHover: true,
            showProgress: true,
            duration: 3,
          });
          return true;
        }
        return false;
      } catch (error: any) {
        const errorMessage = extractErrorMessage(
          error,
          'Erro ao transferir usuários entre planos'
        );
        notification.error({
          message: 'Erro',
          description: errorMessage,
          placement: 'topRight',
          pauseOnHover: true,
          showProgress: true,
          duration: 5,
        });
        return false;
      }
    },
    []
  );

  // Resetar estado quando o usuário fizer logout
  useEffect(() => {
    if (!user?.id) {
      setUserCommercialPlan(null);
      setIsUserPlanLoaded(false);
    }
  }, [user?.id]);

  // Carregar plano comercial do usuário quando ele fizer login
  useEffect(() => {
    const loadUserCommercialPlan = async () => {
      if (logged && user?.id && !isUserPlanLoaded) {
        try {
          const response = await CommercialPlanController.getUserCommercialPlan(
            user.id
          );
          if (response.success) {
            setUserCommercialPlan(response.data);
            setIsUserPlanLoaded(true);
          }
        } catch (error: any) {
          const errorMessage = extractErrorMessage(
            error,
            'Erro ao carregar plano comercial do usuário'
          );
          notification.error({
            message: 'Erro',
            description: errorMessage,
            placement: 'topRight',
            pauseOnHover: true,
            showProgress: true,
            duration: 5,
          });
        }
      }
    };

    loadUserCommercialPlan();
  }, [user?.id, isUserPlanLoaded]);

  useEffect(() => {
    // Só executa se o usuário estiver logado e for do tipo ADM
    if (user && user.typeUser === TypeUser.ADM) {
      handleFetch();
    }
  }, [user, handleFetch]);

  return (
    <CommercialPlanContext.Provider
      value={{
        commercialPlans,
        setCommercialPlans,
        orderBy,
        setOrder,
        filter,
        setFilter,
        isLoading,
        isFetchingNextPage,
        hasNextPage,
        userCommercialPlan,
        setUserCommercialPlan,
        handleFetch,
        handleFetchNextPage,
        handleCreatePlan,
        handleUpdatePlan,
        handleRemovePlan,
        handleGetPlanFontRules,
        handleAddFontRule,
        handleUpdateFontRule,
        handleRemoveFontRule,
        handleSetAsDefault,
        handleActivate,
        handleDeactivate,
        handleGetPlanUsers,
        handleAddUserToPlan,
        handleRemoveUserFromPlan,
        handleGetUserCommercialPlan,
        handleTransferUsers,
      }}
    >
      {children}
    </CommercialPlanContext.Provider>
  );
};

export const useCommercialPlan: () => CommercialPlanContextProps = () =>
  useContext(CommercialPlanContext);

export default CommercialPlan;
